import { ErrorCodes, GqlValidationError, IAppError } from "config/types";

import { CombinedGraphQLErrors, ServerError, ServerParseError } from "@apollo/client";
import { GraphQLError, GraphQLFormattedError } from "graphql";

import {
  AppErrorOptions,
  AppErrorSource,
  AuthRestErrorSource,
  PlainErrorSource,
} from "./error.input.types";

// ---------------------------------------------------------------------------
// ErrorType
// ---------------------------------------------------------------------------

export type ErrorType =
  "auth" | "validation" | "polling" | "business" | "external" | "system" | "network";

/**
 * A function a subclass can declare as its static `shouldReport` property to
 * control whether instances are reported to Elastic APM.
 *
 * Receives the HTTP status code so network-layer subclasses can vary the
 * answer per request (e.g. skip 401s). When omitted the base class falls back
 * to returning `false`.
 */
export type ShouldReportPolicy = (statusCode?: number) => boolean;

// ---------------------------------------------------------------------------
// Exported code sets — the single source of truth for each error category.
// Subclasses reference these directly via their static `codes` property so
// there is no duplication across the module.
// ---------------------------------------------------------------------------

export const AUTH_ERROR_CODES = new Set<ErrorCodes>([
  "GRAPH_API_UNAUTHORIZED",
  "CREDENTIAL_INCORRECT",
  "CREDENTIAL_INVALID",
  "AUTHENTICATION_THIRD_ATTEMPT",
  "AUTHENTICATION_TENTH_ATTEMPT",
  "BLOCKED",
  "TOKEN_INVALID",
  "TOKEN_PURPOSE_MISMATCH",
  "UNSUCCESSFUL_SESSION_VERIFICATION",
  "DEVICE_UNREGISTERED",
  "DEVICE_VERIFICATION_INCORRECT",
]);

export const VALIDATION_ERROR_CODES = new Set<ErrorCodes>([
  "ACCESS_CODE_EXPIRED",
  "ACCESS_CODE_INCORRECT",
  "CANNOT_REUSE_PASSWORD",
  "DUPLICATE_SSN",
  "INVALID_DATE",
  "PASSWORD_MISMATCH",
  "PASSWORD_PREVIOUSLY_USED",
  "PHONE_NUMBER_MISSING",
  "PHONE_NUMBER_NOT_VERIFIED",
  "REQUIRED_INPUT_MISSING",
]);

export const POLLING_ERROR_CODES = new Set<ErrorCodes>([
  "APPLICATION_DOCUMENT_NOT_READY",
  "DYNAMIC_FORMS_POLLING",
]);

export const BUSINESS_ERROR_CODES = new Set<ErrorCodes>([
  "FINANCE_APPLICATION_DISABLED",
  "UNDERWRITING_DISABLED",
  "USER_DECOMMISSIONED",
]);

export const EXTERNAL_ERROR_CODES = new Set<ErrorCodes>(["AURA_COMMON_ERROR"]);

export const SYSTEM_ERROR_CODES = new Set<ErrorCodes>(["STANDARD_ERROR", "RETRY_TIMEOUT_EXCEEDED"]);

// ---------------------------------------------------------------------------
// Internal registry — ordered list used to classify codes into error types.
// SystemError is intentionally absent: it is the default fallback.
// ---------------------------------------------------------------------------

interface ITypeRegistryEntry {
  codes: Set<string>;
  type: ErrorType;
}

const TYPE_REGISTRY: ITypeRegistryEntry[] = [
  { codes: AUTH_ERROR_CODES, type: "auth" },
  { codes: VALIDATION_ERROR_CODES, type: "validation" },
  { codes: POLLING_ERROR_CODES, type: "polling" },
  { codes: BUSINESS_ERROR_CODES, type: "business" },
  { codes: EXTERNAL_ERROR_CODES, type: "external" },
  { codes: SYSTEM_ERROR_CODES, type: "system" },
];

// ---------------------------------------------------------------------------
// Internal extraction result shape
// ---------------------------------------------------------------------------

interface IExtracted {
  code: string;
  fullMessage: string;
  validationErrors?: GqlValidationError[];
  type: ErrorType;
  statusCode?: number;
}

// ---------------------------------------------------------------------------
// Subclass constructor helper
// ---------------------------------------------------------------------------

/**
 * Normalizes the flexible subclass constructor arguments into the two-argument
 * form expected by AppError's constructor.
 *
 * Call it spread inside super() so that super() remains the first statement
 * in subclass constructors, satisfying TS2401.
 *
 * @example
 *   super(...normalizeSubclassArgs(source, optionsOrMessage, validationErrors));
 */
export function normalizeSubclassArgs(
  source: unknown,
  optionsOrMessage?: AppErrorOptions | string,
  validationErrors?: GqlValidationError[]
): [unknown, AppErrorOptions | undefined] {
  if (typeof source === "string") {
    const fullMessage =
      typeof optionsOrMessage === "string"
        ? optionsOrMessage
        : (optionsOrMessage?.fullMessage ?? "");
    return [{ code: source, fullMessage, validationErrors }, undefined];
  }
  return [source as AppErrorSource | AppError, optionsOrMessage as AppErrorOptions | undefined];
}

// ---------------------------------------------------------------------------
// Thunk dispatch result shape
// ---------------------------------------------------------------------------

/**
 * Accepted input shape for `AppError.fromThunk`.
 *
 * `await dispatch(asyncThunkAction())` can resolve to two distinct types
 * depending on which version of the RTK type-inference path TypeScript takes:
 *
 *   • `PayloadAction<T>` — the settled fulfilled/rejected action object
 *     (RTK resolves the type correctly in most call sites).
 *   • `AsyncThunkAction<...>` — the raw thunk *function* (RTK infers the
 *     dispatch return as the thunk itself in some call sites).
 *
 * The callable branch prevents TS2559 ("no properties in common") for the
 * function-typed cases; `(...args: never[]) => void` is the minimal callable
 * type that every function is structurally assignable to (contravariant
 * `never` parameters + void return). The object branch handles settled
 * actions via `ThunkDispatchResultError<T>`; the generic `T` accepts any payload type.
 * The default `T = IAppError` reflects what every `rejectWithValue` in this
 * codebase actually stores.
 */
type ThunkDispatchResult<T = IAppError> =
  ((...args: never[]) => void) | ThunkDispatchResultError<T>;

/** Settled shape of an RTK async thunk dispatch result. */
type ThunkDispatchResultError<T = IAppError> = { error?: { message?: string }; payload?: T };

// ---------------------------------------------------------------------------
// AppError
// ---------------------------------------------------------------------------

/**
 * The single error class for this application.
 *
 * Extends native Error so instances can be thrown anywhere.
 * Accepts any error shape observed in the codebase as its first argument
 * and automatically infers the error type, code, and message from it.
 * Falls back to "system" type for unrecognized shapes or codes.
 *
 * Usage:
 *   throw new AppError(err)
 *   throw new AppError(err, { fullMessage: "Custom message." })
 *   return rejectWithValue(new AppError(err).toPlainObject())
 *   throw new AppError({ code: "CREDENTIAL_INCORRECT", fullMessage: "..." })
 */
export class AppError extends Error implements IAppError {
  /**
   * Subclasses may declare their own static `shouldReport` function to control
   * whether instances are sent to Elastic APM. When absent the getter returns
   * `false` by default — subclasses that want reporting must opt in explicitly.
   *
   * @example
   *   static readonly shouldReport = () => true;                    // ExternalError
   *   static readonly shouldReport = (s?: number) => s !== 401;     // AppNetworkError
   */
  static readonly shouldReport?: ShouldReportPolicy;

  readonly code: string;
  readonly fullMessage: string;
  readonly validationErrors?: GqlValidationError[];
  readonly type: ErrorType;
  /** HTTP status code — only populated for network errors. */
  readonly statusCode?: number;

  constructor(source: unknown, options?: AppErrorOptions) {
    const extracted = AppError._extract(source, options);

    super(extracted.fullMessage || extracted.code);

    this.code = extracted.code;
    this.fullMessage = extracted.fullMessage;
    this.type = extracted.type;

    if (extracted.validationErrors?.length) {
      this.validationErrors = extracted.validationErrors;
    }
    if (extracted.statusCode !== undefined) {
      this.statusCode = extracted.statusCode;
    }

    Object.setPrototypeOf(this, new.target.prototype);
    this.name = new.target.name;
  }

  /**
   * Whether this error should be reported to Elastic APM.
   * Consults the concrete class's static `shouldReport` policy first; falls
   * back to the type-based logic in `_derivesShouldReport` when none is set.
   */
  get shouldReport(): boolean {
    const policy = (this.constructor as typeof AppError).shouldReport;
    return AppError._derivesShouldReport(this.type, this.statusCode, policy);
  }

  /**
   * Extracts an AppError from a Redux Toolkit thunk dispatch result.
   *
   * RTK sets `action.error.message` to "Rejected" whenever a thunk is
   * rejected via `rejectWithValue()`. When that marker is present the
   * payload is the serialised IAppError written by the action; this
   * method reconstitutes it into a live AppError instance so callers
   * get full access to instance methods (shouldReport, isPolling, …).
   *
   * Returns null for fulfilled actions or anything else that is not a
   * rejectWithValue rejection.
   */
  static fromThunk<T = IAppError>(thunkResult: ThunkDispatchResult<T>): AppError | null {
    const action = thunkResult as ThunkDispatchResultError<T>;
    if (action.error?.message !== "Rejected") {
      return null;
    }
    return new AppError(action.payload as AppErrorSource);
  }

  /**
   * Returns the given value as an AppError without double-wrapping.
   *
   * If the source is already an AppError, it is returned as-is (preserving its
   * subclass, stack trace, and all properties). Otherwise the source is wrapped
   * in a new AppError. Prefer this over `new AppError(err)` in catch blocks
   * where the caught value may already be an AppError.
   */
  static from(source: unknown): AppError {
    if (source instanceof AppError) {
      return source;
    }
    return new AppError(source as AppErrorSource);
  }

  // ---------------------------------------------------------------------------
  // Private static helpers
  // ---------------------------------------------------------------------------

  private static _derivesShouldReport(
    type: ErrorType,
    statusCode?: number,
    policy?: ShouldReportPolicy
  ): boolean {
    if (typeof policy === "function") {
      return policy(statusCode);
    }

    // Type-based fallback when base AppError is used directly (no subclass policy).
    // Subclasses should declare their own shouldReport for explicit control.
    if (type === "system" || type === "external") {
      return true;
    }
    if (type === "network") {
      return statusCode !== 401;
    }
    return false;
  }

  private static _extract(source: unknown, options?: AppErrorOptions): IExtracted {
    const raw = AppError._extractFromSource(source);

    return {
      code: options?.code ?? raw.code,
      fullMessage: options?.fullMessage ?? raw.fullMessage,
      statusCode: raw.statusCode,
      type: raw.type,
      validationErrors: options?.validationErrors ?? raw.validationErrors,
    };
  }

  private static _extractFromSource(source: unknown): IExtracted {
    // 1. Already an AppError — passthrough, preserving existing classification
    if (source instanceof AppError) {
      return {
        code: source.code,
        fullMessage: source.fullMessage,
        statusCode: source.statusCode,
        type: source.type,
        validationErrors: source.validationErrors,
      };
    }

    // 2. Apollo GraphQL errors
    if (CombinedGraphQLErrors.is(source)) {
      return AppError._extractFromGraphQLSource(source as CombinedGraphQLErrors);
    }

    // 3. Single GraphQLError from the graphql package — has extensions and extends Error.
    //    Must be checked before the generic Error branch below.
    if (source instanceof GraphQLError) {
      return AppError._extractFromSingleGraphQLError(source);
    }

    // 4. Apollo ServerError/ServerParseError - has statusCode
    if (source instanceof ServerError || source instanceof ServerParseError) {
      const statusCode = source.statusCode;
      return {
        code: `NETWORK_ERROR_${statusCode}`,
        fullMessage: source.message,
        statusCode,
        type: "network",
      };
    }

    // 5. Auth REST error: { response: { data: { error, error_description } } }
    if (
      typeof source === "object" &&
      source !== null &&
      "response" in source &&
      (source as AuthRestErrorSource).response?.data?.error
    ) {
      const s = source as AuthRestErrorSource;
      return AppError._classifyByCode(
        s.response.data.error,
        s.response.data.error_description ?? ""
      );
    }

    // 6. Generic JS Error
    if (source instanceof Error) {
      return {
        code: "STANDARD_ERROR",
        fullMessage: source.message,
        type: "system",
      };
    }

    // 7. Plain error source: { code, fullMessage?, validationErrors? }
    if (typeof source === "object" && source !== null && "code" in source) {
      const s = source as PlainErrorSource;
      return AppError._classifyByCode(s.code, s.fullMessage ?? "", s.validationErrors);
    }

    // 8. Fallback — unrecognized shape
    return {
      code: "STANDARD_ERROR",
      fullMessage: "An unexpected error occurred.",
      type: "system",
    };
  }

  private static _extractFromGraphQLSource(source: CombinedGraphQLErrors): IExtracted {
    const firstError = source.errors?.[0];
    if (!firstError) {
      return {
        code: "STANDARD_ERROR",
        fullMessage: "An unexpected error occurred.",
        type: "system",
      };
    }
    return AppError._extractFromSingleGraphQLError(firstError);
  }

  private static _extractFromSingleGraphQLError(
    err: GraphQLError | GraphQLFormattedError
  ): IExtracted {
    const code = err.message;
    const ext = err.extensions as Record<string, unknown> | undefined;
    const fullMessage = (ext?.full_message as string | undefined) ?? "";
    const validationErrors = (
      ext?.validation_errors as { errors: GqlValidationError[] } | undefined
    )?.errors;
    return AppError._classifyByCode(code, fullMessage, validationErrors);
  }

  private static _classifyByCode(
    code: string,
    fullMessage: string,
    validationErrors?: GqlValidationError[]
  ): IExtracted {
    for (const entry of TYPE_REGISTRY) {
      if (entry.codes.has(code)) {
        return { code, fullMessage, validationErrors, type: entry.type };
      }
    }
    // Unknown codes fall through to "system" — they are reported to APM by default
    return { code, fullMessage, validationErrors, type: "system" };
  }

  /**
   * Returns a plain IAppError object safe for Redux rejectWithValue payloads
   * and any other code expecting the legacy interface shape.
   */
  toPlainObject(): IAppError {
    const result: IAppError = {
      code: this.code,
      fullMessage: this.fullMessage,
    };
    if (this.validationErrors) {
      result.validationErrors = this.validationErrors;
    }
    return result;
  }

  /**
   * Returns true only for polling/retry sentinel errors.
   * Overridden by PollingError to return true.
   */
  isPolling(): boolean {
    return this.type === "polling";
  }
}
